import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2, RefreshCw, Check, Mail, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { authClient, useSession } from "@/lib/auth-client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function OTPInput({
  value,
  onChange,
  disabled,
  onComplete,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  onComplete: (value: string) => void;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, char: string) => {
    if (!/^\d*$/.test(char)) return;

    const newValue = value.split("");
    newValue[index] = char;
    const joined = newValue.join("").slice(0, 6);
    onChange(joined);

    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }

    if (joined.length === 6) {
      onComplete(joined);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setFocusedIndex(index - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted);
    if (pasted.length === 6) {
      onComplete(pasted);
    }
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className="flex justify-center gap-3">
      {Array.from({ length: 6 }).map((_, index) => {
        const isFilled = !!value[index];
        const isFocused = focusedIndex === index;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="relative"
          >
            <input
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={value[index] || ""}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onFocus={() => setFocusedIndex(index)}
              onPaste={handlePaste}
              disabled={disabled}
              className={cn(
                "w-12 h-14 text-center text-2xl font-bold rounded-xl",
                "border-2 bg-background transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-primary/20",
                isFocused && "border-primary",
                isFilled && !isFocused && "border-primary/50 bg-primary/5",
                !isFilled && !isFocused && "border-border",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            />
          </motion.div>
        );
      })}
    </div>
  );
}

export default function VerifyOTP() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending: isSessionLoading, refetch: refetchSession } = useSession();
  const email = (location.state as { email?: string })?.email;

  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [hasAttemptedVerification, setHasAttemptedVerification] = useState(false);

  // Redirect if no email (came directly to this page)
  useEffect(() => {
    if (!email) {
      navigate("/login", { replace: true });
    }
  }, [email, navigate]);

  // If user is already authenticated and hasn't just completed verification, redirect
  useEffect(() => {
    if (!isSessionLoading && session?.user && !verificationSuccess && !isVerifying && !hasAttemptedVerification) {
      navigate("/", { replace: true });
    }
  }, [session, isSessionLoading, verificationSuccess, isVerifying, hasAttemptedVerification, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerify = async (code: string) => {
    if (code.length !== 6) return;

    setIsVerifying(true);
    setHasAttemptedVerification(true);
    setVerificationSuccess(true); // Set immediately to prevent redirect race condition

    try {
      const result = await authClient.signIn.emailOtp({
        email: email!,
        otp: code,
      });

      if (result.error) {
        setVerificationSuccess(false);
        setHasAttemptedVerification(false);
        toast.error(result.error.message ?? "Invalid verification code");
        setOtp("");
        setIsVerifying(false);
        return;
      }

      toast.success("Successfully signed in!");

      // Refetch session to ensure it's properly established before navigating
      try {
        await refetchSession();
      } catch (e) {
        console.log("Session refetch completed");
      }

      // Navigate after a short delay for UI feedback
      await new Promise(resolve => setTimeout(resolve, 500));

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Verification error:", error);
      setVerificationSuccess(false);
      setHasAttemptedVerification(false);
      toast.error("Something went wrong. Please try again.");
      setOtp("");
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !email) return;

    setIsResending(true);

    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "sign-in",
      });

      if (result.error) {
        toast.error(result.error.message ?? "Failed to resend code");
        return;
      }

      toast.success("New verification code sent!");
      setResendCooldown(60);
    } catch (error) {
      console.error("Resend error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        <div className="flex justify-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-7 h-7 text-primary" />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {verificationSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-4"
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold">Welcome back!</h2>
              <p className="text-muted-foreground">Redirecting to dashboard...</p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-8"
            >
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-semibold">Check your email</h2>
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span>{email}</span>
                </div>
              </div>

              <OTPInput
                value={otp}
                onChange={setOtp}
                disabled={isVerifying}
                onComplete={handleVerify}
              />

              <Button
                onClick={() => handleVerify(otp)}
                disabled={otp.length !== 6 || isVerifying}
                className="w-full h-12"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </Button>

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Didn't receive the code?
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResend}
                  disabled={isResending || resendCooldown > 0}
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : resendCooldown > 0 ? (
                    `Resend in ${resendCooldown}s`
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend code
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="text-center text-xs text-muted-foreground mt-8">
          The code expires in 10 minutes. Check your spam folder if you don't see it.
        </p>
      </motion.div>
    </div>
  );
}
